import React, { useState } from 'react';
import {
  AttachFile,
  EventAvailable,
  Home,
  FormatListBulleted,
  RestartAlt,
} from '@mui/icons-material';
import { FormikHelpers, getIn } from 'formik';
import { useEmailForm } from './useEmailForm';
import {
  Typography,
  InputAdornment,
  Tooltip,
  IconButton,
  Theme,
} from '@mui/material';
import { statesConfig } from '@centerpiece/components';
import { Project, Tag } from 'store/api/models';
import { event, useGetTagsQuery } from 'store/api';
import { useCostBreakDown } from './jfield/useCostBreakDown';
import { useAddress } from './jfield/useAddress';

export const useEventForm = ({
  events = [],
  tags,
  contacts = [],
  onDownload,
  setOpenContact,
  preview = false,
  reply = false,
  replied = false,
  forward = false,
  disabled,
  project,
  title,
  addDateRange,
  noResponseDue,
  editing = false,
  notice = false,
}: {
  events?: any[];
  tags?: any[];
  contacts?: any[];
  onDownload: Function;
  setOpenContact?: Function;
  preview?: boolean;
  reply?: boolean;
  replied?: boolean;
  forward?: boolean;
  disabled?: boolean;
  project?: Project;
  title?: string;
  addDateRange?: boolean;
  noResponseDue?: boolean;
  editing?: boolean;
  notice?: boolean;
}) => {
  preview = preview || reply;
  const messageBodyForm = useEventBodyForm({
    events,
    tags,
    contacts,
    onDownload,
    setOpenContact,
    preview,
    reply,
    replied,
    forward,
    disabled,
    project,
    title,
    addDateRange,
    editing,
  });
  const coreEventFields = [
    {
      name: 'event-location',
      label: 'Event Location',
      itemType: 'typography',
      disabled: preview || reply,
      jItemProps: { component: 'div', variant: 'caption' },
    },
    {
      name: 'event-location-bottom',
      label: '',
      itemType: 'divider',
      disabled: preview || reply,
      gridProps: {
        sx: { paddingTop: '0px !important', paddingBottom: 1 },
      },
    },
    {
      precondition: (values: any) => !getIn(values, 'form.copy_project'),
      name: 'form.address',
      itemType: 'textfield',
      disabled: preview || reply,
      label: 'Address',
      gridProps: { xs: preview || reply ? 12 : 9 },
    },
    {
      precondition: (values: any) => getIn(values, 'form.copy_project'),
      name: 'form.address',
      itemType: 'textfield',
      disabled: true,
      defaultValue: project?.form?.address,
      label: 'Address',
      gridProps: { xs: preview || reply ? 12 : 9 },
    },
    {
      precondition: () => !preview && !disabled && !reply,
      name: 'form.copy_project',
      itemType: 'checkbox',
      label: 'Project location',
      gridProps: { xs: 3 },
      disabled: preview || reply,
      defaultValue: true,
      jItemProps: {
        sx: { fontSize: '14px !important' },
      },
    },
    {
      precondition: (values: any) => !getIn(values, 'form.copy_project'),
      name: 'form.city',
      itemType: 'textfield',
      disabled: preview || reply,
      label: 'City',
      gridProps: { xs: 12, sm: 4 },
    },
    {
      precondition: (values: any) => getIn(values, 'form.copy_project'),
      name: 'form.city',
      itemType: 'textfield',
      disabled: true,
      defaultValue: project?.form?.city,
      label: 'City',
      gridProps: { xs: 12, sm: 4 },
    },
    {
      precondition: (values: any) => !getIn(values, 'form.copy_project'),
      name: 'form.state',
      label: 'State',
      itemType: 'autocomplete',
      disabled: preview || reply,
      options: Object.keys(statesConfig),
      gridProps: { xs: 12, sm: 4 },
    },
    {
      precondition: (values: any) => getIn(values, 'form.copy_project'),
      name: 'form.state',
      label: 'State',
      itemType: 'autocomplete',
      disabled: true,
      defaultValue: project?.form?.state,
      options: Object.keys(statesConfig),
      gridProps: { xs: 12, sm: 4 },
    },
    {
      precondition: (values: any) => !getIn(values, 'form.copy_project'),
      name: 'form.zipcode',
      itemType: 'textfield',
      disabled: preview || reply,
      label: 'Postal Code',
      gridProps: { xs: 12, sm: 4 },
    },
    {
      precondition: (values: any) => getIn(values, 'form.copy_project'),
      name: 'form.zipcode',
      itemType: 'textfield',
      disabled: true,
      defaultValue: project?.form?.zipcode,
      label: 'Postal Code',
      gridProps: { xs: 12, sm: 4 },
    },
    ...(addDateRange
      ? [
          {
            name: 'dates',
            itemType: 'dateorrangepicker',
            jItemProps: {
              rangeOnly: true,
              start_name: 'start_date',
              end_name: 'due_date',
              date_adornment: (
                <small>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    DUE DATE:
                  </Typography>
                </small>
              ),
              date_range_adornment: (
                <small>
                  <Typography variant="caption" sx={{ fontWeight: 700 }}>
                    DATE RANGE:
                  </Typography>
                </small>
              ),
              start_label: 'START',
              end_label: 'END',
              date_label: 'DUE DATE',
              onAccept: (
                value: any,
                values: any,
                formik: FormikHelpers<any>
              ) => {
                formik.setFieldValue('form.daterange.0.start', value?.start);
                formik.setFieldValue('form.daterange.0.end', value?.end);
                formik.setFieldValue('start_date', value?.start);
                formik.setFieldValue('due_date', value?.end);
                formik.setFieldValue('dates', value?.start);
              },
            },
            label: 'Date(s) of event',
          },
        ]
      : []),
  ];
  const eventEmail = useEmailForm({
    contacts,
    onDownload,
    setOpenContact,
    preview,
    reply,
    replied,
    forward,
    disabled,
    ccbcc: addDateRange ? false : undefined,
    noResponseDue,
    messageBodyForm,
    editing,
    additionalHeaderFields: coreEventFields,
  });
  return notice
    ? eventEmail
    : [
        ...coreEventFields,
        {
          precondition: (values: any) => (!preview || reply) && !editing,
          name: '',
          itemType: 'button',
          jItemProps: { type: 'submit' },
          label: 'CREATE EVENT',
          gridProps: { xs: 6, mx: 'auto' },
        },
      ];
};

const useEventBodyForm = ({
  events = [],
  tags,
  contacts = [],
  onDownload,
  setOpenContact,
  preview = false,
  reply = false,
  replied = false,
  forward = false,
  disabled,
  project,
  title,
  addDateRange,
  editing,
}: {
  events?: any[];
  tags?: any[];
  contacts?: any[];
  onDownload: Function;
  setOpenContact?: Function;
  preview?: boolean;
  reply?: boolean;
  replied?: boolean;
  forward?: boolean;
  disabled?: boolean;
  project?: Project;
  title?: string;
  addDateRange?: boolean;
  editing?: boolean;
}) => {
  preview = preview || reply;
  const [selectedTag, setSelectedTag] = useState<{ name: string; id: number }>({
    name: 'All',
    id: -1,
  });
  const costBreakDown = useCostBreakDown({
    name: 'cost_breakdown',
    label: 'Quote Breakdown',
    disabled: disabled && !preview ? disabled : replied,
    reply: reply || preview || replied,
  });

  return [
    {
      name: 'form.body',
      label: 'Email Message',
      itemType: 'textfieldarray',
      disabled: disabled ? disabled : preview,
      gridProps: { xs: 12 },
      jItemProps: {
        minHeight: addDateRange ? '200px' : undefined,
        // fab: {
        //   precondition: (values: any) => preview,
        //   name: 'attach_file',
        //   label: 'Attach file',
        //   icon: <AttachFile fontSize="small" />,
        //   itemTypes: 'file',
        // },
        preamble: {
          precondition: (values: any) => !addDateRange && (preview || replied),
          placeholder: !replied ? 'Add your reply here.' : '',
          disabled: disabled && !preview ? disabled : false,
          // divider: addDateRange ? false : undefined,
          action: {
            precondition: (values: any) => {
              const body = getIn(values, 'form.body') || [];
              return (
                // addDateRange ||
                (body?.filter((i: any) => i.type)?.length === 0 && preview) ||
                (replied && body?.[0]?.attachments?.length > 0)
              );
            },
            name: 'replyattachments',
            itemType: 'dropzone',
            label: 'Attach documents',
            disabled: disabled && !preview ? disabled : replied,
            gridProps: { xs: 12 },
            jItemProps: {
              hideDetails: reply || replied,
              onDownload: onDownload,
              onDelete: editing ? () => {} : undefined,
            },
          },
        },
        placeholder: !addDateRange
          ? `Add email message. 

Hover over the (+) button to insert a call to action. Below are the options:

Date range:
Use this to communicate the available dates that they can select from.

Cost breakdown:
Use this to ask for a breakdown of installation costs.

Share a file:
Use this to share event-related files. Examples include drawings, jobsite information, floor plans, equipment list, etc."
`
          : // : !replied
            // ? 'Add email message.'
            '',
        actionsprecondition: (values: any) =>
          !reply && !replied && !addDateRange,
        actions: {
          // address: {
          //   name: 'address',
          //   label: 'Add address',
          //   icon: <Home fontSize="small" />,
          //   form: address,
          //   disabled: true,
          // },
          share_file: {
            name: 'share_file',
            label: 'Share file',
            icon: <AttachFile fontSize="small" />,
            // precondition: (values: any) => {
            //   const action = getIn(values, values.name);
            //   return (
            //     addDateRange ||
            //     editing ||
            //     // (reply && !replied) ||
            //     (action &&
            //       action?.attachments &&
            //       action.attachments?.length > 0) ||
            //     (values &&
            //       values?.attachments &&
            //       values.attachments?.length > 0)
            //   );
            // },
            form: {
              name: 'actions',
              itemType: 'paper',
              label: 'coordination items',
              arrayProps: { wrap: true },
              defaultValue: [{}],
              jItemProps: {
                paper: { elevation: 0 },
              },
              array: [
                {
                  name: 'attachments',
                  itemType: 'dropzone',
                  label: 'Attach documents',
                  disabled: disabled ? disabled : preview || replied,
                  gridProps: { xs: 12 },
                  jItemProps: {
                    hideDetails: reply || replied,
                    onDownload: onDownload,
                    onDelete: editing ? () => {} : undefined,
                  },
                },
                {
                  precondition: (values: any) => {
                    const action = getIn(values, values.name);
                    return (
                      !addDateRange &&
                      (preview ||
                        (replied &&
                          action?.reactionattachments &&
                          action.reactionattachments?.length > 0))
                    );
                  },
                  name: 'share-file-reply-options',
                  label: replied ? 'Replied attachments' : 'Reply attachments',
                  itemType: 'typography',
                  jItemProps: { variant: 'overline' },
                  gridProps: {
                    sx: { marginTop: '0px !important' },
                  },
                },
                {
                  precondition: (values: any) => {
                    const action = getIn(values, values.name);
                    return (
                      !addDateRange &&
                      (preview ||
                        (replied &&
                          action?.reactionattachments &&
                          action.reactionattachments?.length > 0))
                    );
                  },
                  name: 'reactionattachments',
                  itemType: 'dropzone',
                  label: 'Attach documents',
                  disabled: disabled ? disabled : replied,
                  gridProps: { xs: 12 },
                  jItemProps: {
                    hideDetails: reply || replied,
                    onDownload: onDownload,
                  },
                },
                {
                  precondition: () => !addDateRange,
                  name: 'reactiondetails',
                  itemType: 'textfield',
                  label:
                    'Please add any comments on the above attachment(s) here',
                  gridProps: { xs: 12 },
                  jItemProps: {
                    multiline: true,
                    minRows: 3,
                  },
                  disabled: disabled ? disabled : !preview,
                },
              ],
            },
          },
          cost_breakdown: {
            name: 'cost_breakdown',
            label: 'Cost breakdown',
            icon: <FormatListBulleted fontSize="small" />,
            form: costBreakDown,
          },
          date_range: {
            disabled: addDateRange,
            name: 'date_range',
            label: 'Suggest date range',
            icon: <EventAvailable fontSize="small" />,
            form: {
              name: 'actions',
              itemType: 'paper',
              label: 'coordination items',
              arrayProps: { wrap: true },
              defaultValue: [{}],
              jItemProps: {
                paper: { elevation: 0 },
              },
              array: [
                {
                  precondition: () => !reply && !preview && !replied,
                  name: 'title',
                  itemType: 'textfield',
                  label: 'Title',
                },
                {
                  precondition: (values: any) => {
                    return (
                      getIn(values, `${values.name}.title`) &&
                      (reply || preview || replied)
                    );
                  },
                  name: 'title',
                  itemType: 'typography',
                  jItemProps: {
                    variant: 'h5',
                  },
                  label: '',
                },
                {
                  name: 'daterange',
                  itemType: 'daterangepicker',
                  label: 'Availability?',
                  disabled: !preview && disabled ? disabled : replied,
                  jItemProps: {
                    events: events.filter(e =>
                      !e?.extendedProps?.node?.grouping && selectedTag.id > 0
                        ? e?.extendedProps?.node?.tags?.find(
                            (t: any) => t.tag === selectedTag.id
                          )
                        : true
                    ),
                    filters: [
                      {
                        options: [{ id: -1, name: 'All' }, ...(tags || [])],
                        label: 'Filter calendar by tag',
                        defaultOption: selectedTag,
                        onChange: (event: any, newValue: any) =>
                          setSelectedTag(newValue),
                      },
                    ],
                    reply: preview,
                  },
                },
              ],
            },
          },
        },
      },
    },
  ];
};
